import { Immutable } from "immer";
import React from "react";
import { CustomerReviewsBlock } from "tsi-common-react/src/components/CustomerReviewsBlock/index";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CardSliderBlock } from "../../blocks/CardSlider";
import { ProductFeatureBlock } from "../../blocks/ProductFeatureBlock";
import { ProductInspirationBlock } from "../../blocks/ProductInspirationBlock";
import { PromoProductBlock } from "../../blocks/PromoProductBlock";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * toppers HomePage ``home_page_props`` body assembly: dispatches every block
 * type in ``HomePageBodyContent`` to its React component, collapsing N
 * ``{% include_block %}`` hydration roots into a single React render tree.
 *
 * FAITHFUL port of the ``cms/home_page.html`` body loop: a plain
 * ``{% for block in self.home_page_props %}{% include_block block %}{% endfor %}``.
 * The loop's per-type branches (``product_Feature_block`` / ``promo_product_block``
 * / ``product_inspiration_block`` / ``text_columns_with_images``) and the
 * ``{% else %}`` ALL did the identical ``{% include_block block %}`` -- so every
 * one of the 16 block types renders, and nothing is dropped.
 *
 * NO PER-BLOCK WRAPPER (load-bearing): the loop emitted each block via a
 * bare ``{% include_block block %}`` (NOT the whole-field ``{{ self.home_page_props }}``),
 * so Wagtail's ``StreamBlock.render_basic`` ``<div class="w-block-...">`` wrapper
 * was NEVER applied. We reproduce that raw concatenation with
 * ``tagName={React.Fragment}`` (no wrapper element per block) -- contrast the
 * UtilityPage assembly, which DID use ``{{ self.body }}`` and therefore wraps.
 *
 * The surrounding ``home-page-topper-feature-container`` / ``scroll-trigger``
 * divs are PAGE chrome retained in ``home_page.html`` around the
 * ``{% include_react "...HomePageBodyTemplate" %}`` call -- intentionally NOT part
 * of this assembly.
 *
 * Block components are imported DIRECTLY (not via their site-local
 * ``XTemplate.tsx`` re-exports). All five home/shared components share the
 * ``Immutable<{ value: Value }>`` signature, so they slot into the StreamField
 * component map as plain references; the 11 Toolbox keys come from the shared
 * ``toolboxComponentsMap``.
 */

export const homePageComponentsMap = {
    ...toolboxComponentsMap,
    // 4 home blocks -- note the EXACT `product_Feature_block` key
    // (capital F) preserved verbatim from the Python block name.
    product_Feature_block: ProductFeatureBlock,
    promo_product_block: PromoProductBlock,
    product_inspiration_block: ProductInspirationBlock,
    // `text_columns_with_images` -> the CardSlider block component (exported as
    // `CardSliderBlock`).
    text_columns_with_images: CardSliderBlock,
    // 1 shared block (`SharedCustomerReviewsBlock` value) -> the shared
    // CustomerReviewsBlock component.
    customer_reviews_block: CustomerReviewsBlock,
} as const;

export const HomePageBody = (
    props: Immutable<{
        page: Pick<templates.HomePageTemplate["page"], "home_page_props">;
    }>,
): React.JSX.Element | null => {
    const homePageProps = props.page.home_page_props;
    if (!homePageProps) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={homePageProps.value}
            components={homePageComponentsMap}
        />
    );
};
